import {
    ISelectedFilterItem,
    ISharingMenuData,
} from '@fi-shared/fi-items-list/fi-items-list-filter/fi-items-list-filter/fi-items-list-filter';
import { FiFilterType } from '@fi-shared/fi-items-list/fi-items-list-filter/fi-filter-type-enum';
import { Injector } from '@angular/core';
import { AppTextService } from '@fi-shared/app-text';
import { ILightWeightArea, ILightWeightObject, ILightWeightUser } from '@fi-shared/models';
import { IIdTextId, INameIdObj, INameIdObjWithChildren } from '@fi-shared/fi-shared-interfaces';
import { isDefined } from '@fi-shared/utils';
import {
    IReportAvailableItemsBase,
    IReportAvailableItemsByData,
    IReportFilterBase,
    IReportMainItem,
} from '@fi-shared/fi-report-table-filter/fi-report-filter-base';
import { IBusinessUnitForReport } from '@fi-shared/checklist/fi-checklist2-report/shared/fi-checklist2-report';
import { fiIntersection } from '@fi-shared/utils/fi-intersection';
import { fiExecuteOnChildItems, fiItemsFlatten } from '@fi-shared/utils/fi-items-flatten';

interface IFilterMenuCreateArgsBase {
    labelTextId?: string;
    items?: INameIdObj[];
    users?: ILightWeightUser[];
    areas?: ILightWeightArea[];
    isAvailable?: boolean;
    isOptionHidden?: (item: ILightWeightObject) => boolean;
}

export abstract class FiReportTableFilterHelperBase {
    protected appTextService: AppTextService;

    protected constructor(private injector: Injector) {
        this.appTextService = this.injector.get(AppTextService);
    }

    //# region selected menu items
    updatedMenusDataWithSelectedItems(
        menusData: ISharingMenuData<INameIdObj>[],
        selectedFilterItemsGroups: { [key: number]: ISelectedFilterItem[] },
    ) {
        // todo looks like it works without returning menusData
        return menusData.map((menu) => {
            const selectedItemsByType = selectedFilterItemsGroups[menu.filterType] || [];
            const selectedItemIds: number[] = selectedItemsByType
                .map((item) => {
                    if (typeof item.value === 'number') {
                        return item.value;
                    }
                    if (typeof item.value === 'string') {
                        // special case for task names
                        return item.value as any;
                    }
                })
                .filter((id) => isDefined(id));

            if (isDefined(menu.selectedChildItemIds)) {
                // special case, areas for example
                menu.selectedItemIds = menu.selectedChildItemIds = [];
                menu.menuItems.forEach((menuItem) => {
                    if (selectedItemIds.includes(menuItem.Id)) {
                        menu.selectedItemIds.push(menuItem.Id);
                    }
                    fiExecuteOnChildItems(menuItem as INameIdObjWithChildren, (childItem) => {
                        if (selectedItemIds.includes(childItem.Id)) {
                            menu.selectedItemIds.push(childItem.Id);
                        }
                    });
                });
            } else {
                menu.selectedItemIds = selectedItemIds;
            }
            //return menu;
        });
    }

    getSelectedFilterItemsFromAvailableItems(
        selectedItemIds: number[],
        availableItems: INameIdObj[],
        filterType: FiFilterType,
    ): ISelectedFilterItem[] {
        return (
            selectedItemIds?.map((id) => {
                return {
                    value: id,
                    name: availableItems.find((r) => r.Id === id)?.Name || '',
                    filterType: filterType,
                } as ISelectedFilterItem;
            }) || []
        );
    }

    getSelectedFilterItemsFromAvailableUsers(
        selectedUserIds: number[],
        availableUsers: ILightWeightUser[],
    ): ISelectedFilterItem[] {
        return (
            selectedUserIds?.map((id) => {
                return {
                    value: id,
                    name: availableUsers.find((r) => r.Id === id)?.FullName || '',
                    filterType: FiFilterType.User,
                } as ISelectedFilterItem;
            }) || []
        );
    }

    getSelectedFilterItemsFromEnum(
        selectedEnumValues: number[],
        enumItems: IIdTextId[],
        filterType: FiFilterType,
    ): ISelectedFilterItem[] {
        return (
            selectedEnumValues?.map((enumValue) => {
                return {
                    value: enumValue,
                    name: this.appTextService.get(
                        enumItems.find((enumItem) => enumItem.id === enumValue)?.textId || '',
                    ),
                    filterType: filterType,
                } as ISelectedFilterItem;
            }) || []
        );
    }

    getSelectedFilterItemsFromStrings(selectedStringValues: string[], filterType: FiFilterType): ISelectedFilterItem[] {
        return (
            selectedStringValues?.map((stringValue) => {
                return {
                    value: stringValue,
                    name: stringValue,
                    filterType: filterType,
                } as ISelectedFilterItem;
            }) || []
        );
    }

    getValuesFromSelectedFilterItems(itemGroup: ISelectedFilterItem[]): number[] {
        return itemGroup?.map((item) => item.value as number) || [];
    }

    getValuesStrFromSelectedFilterItems(itemGroup: ISelectedFilterItem[]): string[] {
        return itemGroup?.map((item) => item.value as string) || [];
    }

    //# endregion

    //# region get base menus
    protected getBusinessUnitsMenu(args?: IFilterMenuCreateArgsBase): ISharingMenuData<ILightWeightObject> {
        return {
            label: this.appTextService.get(args?.labelTextId ?? 'common.units'),
            menuItems: args?.items ?? [],
            displayFieldPropertyName: 'Name',
            isOptionHidden: (item) => isDefined(args?.isOptionHidden) && args.isOptionHidden(item),
            filterType: FiFilterType.BusinessUnit,
            selectedItemIds: [],
            isAvailable: () => args?.isAvailable ?? true,
        };
    }

    protected getBusinessAreasMenu(args?: IFilterMenuCreateArgsBase): ISharingMenuData<ILightWeightObject> {
        return {
            label: this.appTextService.get(args?.labelTextId ?? 'common.business_areas'),
            menuItems: args?.items ?? [],
            displayFieldPropertyName: 'Name',
            isOptionHidden: (item) => isDefined(args?.isOptionHidden) && args.isOptionHidden(item),
            filterType: FiFilterType.BusinessArea,
            selectedItemIds: [],
            isAvailable: () => args?.isAvailable ?? true,
        };
    }

    protected getAreasMenu(args?: IFilterMenuCreateArgsBase): ISharingMenuData<ILightWeightArea> {
        return {
            label: this.appTextService.get(args?.labelTextId ?? 'common.areas'),
            menuItems: (args?.areas ?? []).filter((area: ILightWeightArea) => !area.ParentId),

            displayFieldPropertyName: 'Name',
            isOptionHidden: (item) => isDefined(args?.isOptionHidden) && args.isOptionHidden(item),
            filterType: FiFilterType.Area,
            childrenFilterType: FiFilterType.Area,
            selectedItemIds: [],
            selectedChildItemIds: [],
            isSubmenuLayout: true,
            isAvailable: () => args?.isAvailable ?? true,
        };
    }

    protected getUsersMenu(args?: IFilterMenuCreateArgsBase): ISharingMenuData<ILightWeightObject> {
        return {
            label: this.appTextService.get(args?.labelTextId ?? 'common.users'),
            menuItems: (args?.users ?? []).map((user) => ({ Id: user.Id, Name: user.FullName })),
            displayFieldPropertyName: 'Name',
            isOptionHidden: (item) => isDefined(args?.isOptionHidden) && args.isOptionHidden(item),
            filterType: FiFilterType.User,
            selectedItemIds: [],
            isAvailable: () => args?.isAvailable ?? true,
        };
    }

    protected updateBusinessUnitItems(menusData: ISharingMenuData<INameIdObj>[], businessUnits: ILightWeightObject[]) {
        this.getMenusDataItem(menusData, FiFilterType.BusinessUnit).menuItems = businessUnits.map((item) => ({
            Id: item.Id,
            Name: item.Name,
        }));
    }

    protected updateUserItems(menusData: ISharingMenuData<INameIdObj>[], users: ILightWeightUser[]) {
        this.getMenusDataItem(menusData, FiFilterType.User).menuItems = users.map((item) => ({
            Id: item.Id,
            Name: item.FullName,
        }));
    }

    protected getMenusDataItem<T>(menusData: ISharingMenuData<T>[], filterType: FiFilterType): ISharingMenuData<T> {
        const menuDataItem = menusData.filter((item) => item.filterType === filterType)[0];
        if (!menuDataItem) {
            throw new Error(`unknown filterType ${filterType} in getMenusDataItem`);
        }
        return menuDataItem;
    }

    //# endregion

    //#region get available items by data

    filterBusinessUnitsByAreaIds(businessUnits: IBusinessUnitForReport[], areaIds: number[]): IBusinessUnitForReport[] {
        return businessUnits.filter((bu) => areaIds.includes(bu.AreaId));
    }

    filterBusinessUnitsByBusinessAreaIds(
        businessUnits: IBusinessUnitForReport[],
        businessAreaIds: number[],
    ): IBusinessUnitForReport[] {
        return businessUnits.filter((bu) => !!fiIntersection(bu.BusinessAreaIds, businessAreaIds).length);
    }

    public filterBusinessAreasByBusinessUnits(
        businessAreas: ILightWeightObject[],
        units: IBusinessUnitForReport[],
    ): ILightWeightObject[] {
        return businessAreas.filter((businessArea) => units.some((bu) => bu.BusinessAreaIds.includes(businessArea.Id)));
    }

    isMainItemSharedToArea(mainItem: IReportMainItem, areaId: number): boolean {
        return mainItem.AreaIds.includes(areaId);
    }

    isMainItemSharedToBusinessArea(mainItem: IReportMainItem, businessAreaId: number): boolean {
        return mainItem.BusinessAreaIds.includes(businessAreaId);
    }
    isMainItemSharedToBusinessUnit(mainItem: IReportMainItem, businessUnitId: number): boolean {
        return mainItem.BusinessUnitIds.includes(businessUnitId);
    }

    filterBusinessAreasByMainItems(
        businessAreas: ILightWeightObject[],
        mainItems: IReportMainItem[],
    ): ILightWeightObject[] {
        return businessAreas.filter((businessArea) => {
            return mainItems.find(
                (mainItem) =>
                    !mainItem.BusinessAreaIds.length || // schedule/onboarding etc can have no business areas in sharing selection, it means it shared to all business areas
                    this.isMainItemSharedToBusinessArea(mainItem, businessArea.Id),
            );
        });
    }

    filterBusinessUnitsByMainItems(units: IBusinessUnitForReport[], mainItem: IReportMainItem[]) {
        return units.filter((bu) => mainItem.find((mainItem) => this.isMainItemSharedToBusinessUnit(mainItem, bu.Id)));
    }

    filterAreasByBusinessUnits(
        areas: ILightWeightArea[],
        units: IBusinessUnitForReport[],
        mainItems: IReportMainItem[],
    ): ILightWeightArea[] {
        const areaIdsByBusinessUnits = units.map((bu) => bu.AreaId);
        const isAreaAvailableFn = (areaId: number) => {
            return (
                areaIdsByBusinessUnits.includes(areaId) &&
                (!mainItems || mainItems.find((mainItem) => this.isMainItemSharedToArea(mainItem, areaId)))
            ); // selected schedules/onboardings etc can be undefined
        };
        return fiItemsFlatten(areas).filter((area) => {
            // area is available if it is shared to schedule or any of child areas is shared to schedule
            if (isAreaAvailableFn(area.Id)) {
                return true;
            }
            return !!area.ChildItems.find((childArea) => isAreaAvailableFn(childArea.Id));
        }); /*
            .map((area) => {
                if (!!area.ChildItems?.length)
                    area.ChildItems = area.ChildItems.filter((childArea) => isAreaAvailableFn(childArea.Id));
                return area;
            });*/
    }

    getAvailableUnitsAreasBusinessAreas(
        // onboardings, checklists, tasks etc
        mainItems: IReportMainItem[],
        selectedMainItemIds: number[],
        filter: IReportFilterBase,
        availableItems: IReportAvailableItemsBase,
        args?: {
            hideDeactivatedUnits?: boolean;
            availableBusinessUnitIdsByData?: number[];
        },
    ): IReportAvailableItemsByData {
        if (!isDefined(args)) {
            args = {};
        }
        let availableBusinessUnits = availableItems.BusinessUnits;
        let availableAreas = availableItems.Areas;
        let availableBusinessAreas = availableItems.BusinessAreas;

        // note: for different cases we have availableBusinessUnitIds at filter initially available business unit, for example
        // in Attention required checklist details we need to work only with units with deviations and not all availableItems.AvailableBusinessUnits;
        // in Open checklist details - we need to get display only filtered units
        if (!!args.availableBusinessUnitIdsByData?.length) {
            availableBusinessUnits = availableBusinessUnits.filter((bu) =>
                args.availableBusinessUnitIdsByData.includes(bu.Id),
            );
            availableBusinessAreas = this.filterBusinessAreasByBusinessUnits(
                availableBusinessAreas,
                availableBusinessUnits,
            );
        }

        // https://chainformation.atlassian.net/browse/FI-6361
        // hide deactivated units in open checklists summary, deviations, recent activity
        if (args.hideDeactivatedUnits) {
            availableBusinessUnits = availableBusinessUnits.filter((bu) => bu.IsActivated);
        }

        let selectedMainItems: IReportMainItem[];
        if (selectedMainItemIds?.length) {
            // if schedule ids selected we should filter by schedule access rights
            selectedMainItems = mainItems.filter((mainItem) => selectedMainItemIds.includes(mainItem.Id));

            // filter business areas by business units
            availableBusinessAreas = this.filterBusinessAreasByBusinessUnits(
                availableBusinessAreas,
                availableItems.BusinessUnits,
            );
            // filter business areas by schedules/onboardings etc
            availableBusinessAreas = this.filterBusinessAreasByMainItems(availableBusinessAreas, selectedMainItems);

            // filter business unis by schedules/onboardings etc
            availableBusinessUnits = this.filterBusinessUnitsByMainItems(availableBusinessUnits, selectedMainItems);

            // filter business units by business areas
            availableBusinessUnits = this.filterBusinessUnitsByBusinessAreaIds(
                availableBusinessUnits,
                availableBusinessAreas.map((ba) => ba.Id),
            );

            // filter areas by business units and schedules
            availableAreas = this.filterAreasByBusinessUnits(availableAreas, availableBusinessUnits, selectedMainItems);
        }

        if (filter.AreaIds?.length) {
            const selectedAreaIds = fiItemsFlatten(availableItems.Areas)
                .filter((area) => filter.AreaIds.includes(area.Id))
                .map((area) => area.Id);
            availableBusinessUnits = this.filterBusinessUnitsByAreaIds(availableBusinessUnits, selectedAreaIds);
            availableBusinessAreas = this.filterBusinessAreasByBusinessUnits(
                availableBusinessAreas,
                availableBusinessUnits,
            );
        }

        if (filter.BusinessAreaIds?.length) {
            availableBusinessUnits = this.filterBusinessUnitsByBusinessAreaIds(
                availableBusinessUnits,
                filter.BusinessAreaIds,
            );

            const availableUnitsByBusinessAreas = this.filterBusinessUnitsByBusinessAreaIds(
                availableItems.BusinessUnits,
                filter.BusinessAreaIds,
            );
            availableAreas = this.filterAreasByBusinessUnits(
                availableAreas,
                availableUnitsByBusinessAreas,
                selectedMainItems,
            );
        }

        return {
            BusinessUnitIds: availableBusinessUnits.map((item) => item.Id),
            BusinessAreaIds: availableBusinessAreas.map((item) => item.Id),
            AreaIds: availableAreas.map((item) => item.Id),
        };
    }
    //#endregion
}
